export type MasterdataItemKind = 'article' | 'service';
export type MasterdataUnit = 'piece' | 'hour' | 'kilometer' | 'flat-rate' | 'meter' | 'kilogram' | 'liter';
export type MasterdataStatusFilter = 'all' | 'active' | 'inactive';

export interface MasterdataItemRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly kind: MasterdataItemKind;
  readonly name: string;
  readonly description: string;
  readonly unit: MasterdataUnit;
  readonly salesPrice: number;
  readonly purchasePrice: number;
  readonly vatRate: number;
  readonly manufacturer: string;
  readonly manufacturerPartNumber: string;
  readonly barcode: string;
  readonly stockTracked: boolean;
  readonly minimumStock: number;
  readonly active: boolean;
  readonly preferredSupplierId: string | null;
  readonly preferredSupplierName: string;
  readonly supplierCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Zugang: seit wann im Bestand (ISO-Datum). */
  readonly activeSince: string;
  /** Abgang: seit wann nicht mehr im Bestand oder null (noch im Bestand). */
  readonly activeUntil: string | null;
  /** Warengruppe je Artikel/Dienstleistung (Stufe 2, Feinzuordnung) — optional. */
  readonly warengruppeId?: string | null;
  readonly warengruppeName?: string;
  /** Dienstleistung ist eine UVV-Prüfung: beim Abrechnen wird das Maschinen-Prüfdatum +12 Monate gesetzt. */
  readonly isUvv?: boolean;
}

export interface MasterdataItemSearchInput {
  readonly tenantId: string;
  readonly kind: MasterdataItemKind;
  readonly query?: string;
  readonly status?: MasterdataStatusFilter;
  readonly bestandFrom?: string;
  readonly bestandTo?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedMasterdataItemSearchInput {
  readonly tenantId: string;
  readonly kind: MasterdataItemKind;
  readonly query: string;
  readonly status: MasterdataStatusFilter;
  readonly bestandFrom: string;
  readonly bestandTo: string;
  readonly page: number;
  readonly pageSize: number;
}

export interface MasterdataItemPage {
  readonly items: readonly MasterdataItemRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SaveMasterdataItemInput {
  readonly tenantId: string;
  readonly itemId?: string;
  readonly kind: MasterdataItemKind;
  readonly name: string;
  readonly description?: string;
  readonly unit: MasterdataUnit;
  readonly salesPrice?: number;
  readonly purchasePrice?: number;
  readonly vatRate?: number;
  readonly manufacturer?: string;
  readonly manufacturerPartNumber?: string;
  readonly barcode?: string;
  readonly stockTracked?: boolean;
  readonly minimumStock?: number;
  readonly active?: boolean;
  readonly warengruppeId?: string | null;
  readonly isUvv?: boolean;
}

export interface SupplierItemRecord {
  readonly itemId: string;
  readonly itemNumber: string;
  readonly itemName: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
  readonly preferred: boolean;
}

export type ArticleMovementType = 'receipt' | 'issue' | 'transfer' | 'adjustment';

/** Bestand eines Artikels an einem Lagerplatz -- für den „Bestand & Bewegungen"-Tab der Artikelakte. */
export interface ArticleStockLocation {
  readonly locationCode: string;
  readonly locationName: string;
  readonly onHand: number;
  readonly available: number;
  readonly minimumStock: number;
  readonly lowStock: boolean;
}

/** Eine Lagerbewegung eines Artikels (leichtgewichtig, bereits aufgelöst). */
export interface ArticleMovement {
  readonly type: ArticleMovementType;
  readonly quantity: number;
  readonly from: string;
  readonly to: string;
  readonly reason: string;
  readonly reference: string;
  readonly date: string;
}

/** Aggregierter Lagerstand + Bewegungen je Artikel, keyed by Artikelnummer im Loader. */
export interface ArticleStock {
  readonly onHand: number;
  readonly reserved: number;
  readonly available: number;
  readonly lowStock: boolean;
  readonly locations: readonly ArticleStockLocation[];
  readonly movements: readonly ArticleMovement[];
}

/** Ein Lieferant, der einen Artikel führt -- für den „Lieferanten"-Tab der Artikelakte. */
export interface ArticleSupplierLink {
  readonly supplierNumber: string;
  readonly supplierName: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
  readonly preferred: boolean;
}

export interface SupplierRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly name: string;
  readonly vatId: string;
  readonly email: string;
  readonly phone: string;
  readonly website: string;
  readonly contactName: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  readonly notes: string;
  readonly active: boolean;
  readonly itemCount: number;
  readonly items: readonly SupplierItemRecord[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly activeSince: string;
  readonly activeUntil: string | null;
}

export interface SupplierSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly status?: MasterdataStatusFilter;
  readonly bestandFrom?: string;
  readonly bestandTo?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedSupplierSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly status: MasterdataStatusFilter;
  readonly bestandFrom: string;
  readonly bestandTo: string;
  readonly page: number;
  readonly pageSize: number;
}

export interface SupplierPage {
  readonly items: readonly SupplierRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SaveSupplierInput {
  readonly tenantId: string;
  readonly supplierId?: string;
  readonly name: string;
  readonly vatId?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly website?: string;
  readonly contactName?: string;
  readonly street?: string;
  readonly postalCode?: string;
  readonly city?: string;
  readonly countryCode?: string;
  readonly notes?: string;
  readonly active?: boolean;
}

export interface LinkSupplierItemInput {
  readonly tenantId: string;
  readonly supplierId: string;
  readonly itemId: string;
  readonly supplierItemNumber?: string;
  readonly purchasePrice?: number;
  readonly leadTimeDays?: number | null;
  readonly preferred?: boolean;
}

export interface MasterdataGateway {
  searchItems(input: MasterdataItemSearchInput): Promise<MasterdataItemPage>;
  getItem(tenantId: string, itemId: string): Promise<MasterdataItemRecord>;
  saveItem(input: SaveMasterdataItemInput): Promise<MasterdataItemRecord>;
  searchSuppliers(input: SupplierSearchInput): Promise<SupplierPage>;
  getSupplier(tenantId: string, supplierId: string): Promise<SupplierRecord>;
  saveSupplier(input: SaveSupplierInput): Promise<SupplierRecord>;
  searchArticleOptions(tenantId: string, query?: string): Promise<readonly MasterdataItemRecord[]>;
  linkSupplierItem(input: LinkSupplierItemInput): Promise<SupplierRecord>;
  unlinkSupplierItem(tenantId: string, supplierId: string, itemId: string): Promise<SupplierRecord>;
}

const units = new Set<MasterdataUnit>([
  'piece', 'hour', 'kilometer', 'flat-rate', 'meter', 'kilogram', 'liter',
]);

/** The only Umsatzsteuer rates an article or service may carry — exported so the UI matches. */
export const germanVatRates: readonly number[] = [0, 7, 19];

function finiteNonNegative(value: number | undefined, label: string): number {
  const result = value ?? 0;
  if (!Number.isFinite(result) || result < 0) throw new Error(`${label} darf nicht negativ sein.`);
  return Math.round(result * 100) / 100;
}

export function normalizeMasterdataItemSearchInput(
  input: MasterdataItemSearchInput,
): NormalizedMasterdataItemSearchInput {
  return {
    tenantId: input.tenantId,
    kind: input.kind,
    query: input.query?.trim() ?? '',
    status: input.status ?? 'all',
    bestandFrom: input.bestandFrom?.trim() ?? '',
    bestandTo: input.bestandTo?.trim() ?? '',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeSupplierSearchInput(input: SupplierSearchInput): NormalizedSupplierSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    status: input.status ?? 'all',
    bestandFrom: input.bestandFrom?.trim() ?? '',
    bestandTo: input.bestandTo?.trim() ?? '',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeMasterdataItemInput(input: SaveMasterdataItemInput): SaveMasterdataItemInput {
  const name = input.name.trim();
  if (!name) throw new Error('Der Stammdatensatz benötigt eine Bezeichnung.');
  if (!units.has(input.unit)) throw new Error('Die Einheit ist ungültig.');
  const vatRate = input.vatRate ?? 19;
  // Only the German rates: an article carrying e.g. 12 % would pass its rate on to every invoice
  // line built from it, and that line could not be booked. Rejected at the source instead.
  if (!germanVatRates.includes(vatRate)) {
    throw new Error(`${vatRate} % ist kein gültiger Umsatzsteuersatz. Erlaubt sind 0, 7 oder 19 Prozent.`);
  }
  const stockTracked = input.kind === 'article' && Boolean(input.stockTracked);
  return {
    tenantId: input.tenantId,
    ...(input.itemId ? { itemId: input.itemId } : {}),
    kind: input.kind,
    name,
    description: input.description?.trim() ?? '',
    unit: input.unit,
    salesPrice: finiteNonNegative(input.salesPrice, 'Der Verkaufspreis'),
    purchasePrice: input.kind === 'article'
      ? finiteNonNegative(input.purchasePrice, 'Der Einkaufspreis')
      : 0,
    vatRate: Math.round(vatRate * 100) / 100,
    manufacturer: input.kind === 'article' ? input.manufacturer?.trim() ?? '' : '',
    manufacturerPartNumber: input.kind === 'article'
      ? input.manufacturerPartNumber?.trim() ?? ''
      : '',
    barcode: input.kind === 'article' ? input.barcode?.trim() ?? '' : '',
    stockTracked,
    minimumStock: stockTracked ? finiteNonNegative(input.minimumStock, 'Der Mindestbestand') : 0,
    active: input.active ?? true,
    warengruppeId: input.warengruppeId ?? null,
    // UVV-Kennzeichen nur bei Dienstleistungen sinnvoll.
    isUvv: input.kind === 'service' ? Boolean(input.isUvv) : false,
  };
}

export function normalizeSupplierInput(input: SaveSupplierInput): SaveSupplierInput {
  const name = input.name.trim();
  if (!name) throw new Error('Der Lieferant benötigt einen Namen.');
  const countryCode = input.countryCode?.trim().toUpperCase() || 'DE';
  if (!/^[A-Z]{2}$/.test(countryCode)) throw new Error('Der Ländercode muss aus zwei Buchstaben bestehen.');
  return {
    tenantId: input.tenantId,
    ...(input.supplierId ? { supplierId: input.supplierId } : {}),
    name,
    vatId: input.vatId?.trim().toUpperCase() ?? '',
    email: input.email?.trim().toLowerCase() ?? '',
    phone: input.phone?.trim() ?? '',
    website: input.website?.trim() ?? '',
    contactName: input.contactName?.trim() ?? '',
    street: input.street?.trim() ?? '',
    postalCode: input.postalCode?.trim() ?? '',
    city: input.city?.trim() ?? '',
    countryCode,
    notes: input.notes?.trim() ?? '',
    active: input.active ?? true,
  };
}

export function normalizeSupplierItemInput(input: LinkSupplierItemInput): LinkSupplierItemInput {
  const leadTimeDays = input.leadTimeDays ?? null;
  if (leadTimeDays !== null && (!Number.isInteger(leadTimeDays) || leadTimeDays < 0 || leadTimeDays > 3650)) {
    throw new Error('Die Lieferzeit muss eine ganze Zahl zwischen 0 und 3650 Tagen sein.');
  }
  return {
    tenantId: input.tenantId,
    supplierId: input.supplierId,
    itemId: input.itemId,
    supplierItemNumber: input.supplierItemNumber?.trim() ?? '',
    purchasePrice: finiteNonNegative(input.purchasePrice, 'Der Lieferantenpreis'),
    leadTimeDays,
    preferred: Boolean(input.preferred),
  };
}
